/**
 * Archivo: VentanaAñadirColegiales.js
 * Autor: Acher Egido Mairal
 * Fecha de última modificación: 2024-07-10
 * 
 * Descripción:
 * Este componente define una ventana modal para añadir nuevos colegiales manualmente o desde un archivo (CSV, XLSX, XLS).
 * 
 * Dependencias:
 * - react: ^18.2.0
 * - js-cookie: ^2.2.1
 * - xlsx: ^0.17.4
 * 
 * Propósito:
 * Proporcionar una interfaz para que los usuarios puedan añadir colegiales, incluyendo la opción de carga de archivos para la inserción masiva.
 */

import React, { useState } from 'react';
import Cookies from 'js-cookie';
import * as XLSX from 'xlsx';
import '../Estilos/estilos.css';
import { añadirColegial, añadirColegialesDesdeArchivo } from '../Servicios/ColegialesServicios';
import cruzNegra from '../Imagenes/Iconos/CruzNegra.png';

const VentanaAñadirColegiales = ({ onClose, actualizarLista }) => {
    const [archivoImagen, setArchivoImagen] = useState(null);
    const [nombreArchivoImagen, setNombreArchivoImagen] = useState('');
    const [archivoDatos, setArchivoDatos] = useState(null);
    //const [nombreArchivoDatos, setNombreArchivoDatos] = useState('');
    const [mostrarFormularioManual, setMostrarFormularioManual] = useState(false);
    const [mensajeError, setMensajeError] = useState('');
    const [mensajeExito, setMensajeExito] = useState('');

    const handleFormularioManual = () => {
        setMensajeError('');
        setMensajeExito('');
        setMostrarFormularioManual(false);
    };

    const [nuevoColegial, setNuevoColegial] = useState({
        nombre_colegial: '',
        apellidos_colegial: '',
        estudios_colegial: '',
        habitacion_colegial: '',
        genero_colegial: '',
        promocion_colegial: '',
        pasillo_colegial: '',
        tieneCargo_colegial: false,
        telefono_colegial: '',
        correo_colegial: ''
    });

    const manejarArchivosImagenes = (e) => {
        const file = e.target.files[0];
        const tiposValidos = ['image/jpeg', 'image/png', 'image/jpg'];
        if (file && tiposValidos.includes(file.type)) {
            setArchivoImagen(file);
            setNombreArchivoImagen(file.name);
        } else {
            setMensajeExito('');
            setMensajeError('Por favor, selecciona un archivo válido (jpg, png).');
        }
    };

    const manejarArchivoDatos = (e) => {
        const file = e.target.files[0];
        const tiposValidos = ['text/csv', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
        if (file && tiposValidos.includes(file.type)) {
            setArchivoDatos(file);
            //setNombreArchivoDatos(file.name);
        } else {
            setMensajeExito('');
            setMensajeError('Por favor, selecciona un archivo de datos válido (csv, xlsx, xls).');
        }
    };

    const manejarAñadirColegiales = async () => {
        if (!archivoDatos) {
            setMensajeExito('');
            setMensajeError('Por favor, selecciona un archivo de datos.');
            return;
        }

        const reader = new FileReader();
        reader.onload = async (e) => {
            const data = e.target.result;
            let workbook;
            if (archivoDatos.type === 'text/csv') {
                workbook = XLSX.read(data, { type: 'binary' });
            } else {
                workbook = XLSX.read(data, { type: 'array' });
            }

            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });

            const [header, ...rows] = jsonData;

            const colegiales = rows
                .map(row => ({
                    foto_colegial: row[0] || '',
                    nombre_colegial: row[1] || '',
                    apellidos_colegial: row[2] || '',
                    estudios_colegial: row[3] || '',
                    habitacion_colegial: row[4] || '',
                    genero_colegial: row[5] || '',
                    promocion_colegial: row[6] || '',
                    pasillo_colegial: row[7] || '',
                    tieneCargo_colegial: row[8] === 'true' ? true : false,
                    telefono_colegial: row[9] || '',
                    correo_colegial: row[10] || ''
                }))
                .filter(colegial => Object.keys(colegial).some(key => key !== 'tieneCargo_colegial' && colegial[key].trim() !== ''));
            //console.log("Colegiales a enviar: ", colegiales);
            try {
                const token = Cookies.get('token');
                const result = await añadirColegialesDesdeArchivo(token, { colegiales });

                if (result.success) {
                    setMensajeError('');
                    setMensajeExito('Colegiales añadidos con éxito');
                    actualizarLista();
                } else {
                    setMensajeExito('');
                    setMensajeError(result.message);
                }
            } catch (error) {
                setMensajeExito('');
                setMensajeError('Error al añadir colegiales');
            }
        };

        if (archivoDatos.type === 'text/csv') {
            reader.readAsBinaryString(archivoDatos);
        } else {
            reader.readAsArrayBuffer(archivoDatos);
        }
    };
    

    const manejarAñadirColegial = async () => {
        //console.log("Colegial a insertar: ", nuevoColegial);
        setMensajeError('')
        setMensajeExito('')
        try {
            const token = Cookies.get('token');
            const formData = new FormData();
            for (const key in nuevoColegial) {
                formData.append(key, nuevoColegial[key]);
            }
            if (archivoImagen) {
                formData.append('foto_colegial', archivoImagen, nombreArchivoImagen);
            }
            const result = await añadirColegial(token, formData);
            if (result.success) {
                setMensajeExito('Colegial añadido con éxito');
                setNuevoColegial({
                    nombre_colegial: '',
                    apellidos_colegial: '',
                    estudios_colegial: '',
                    habitacion_colegial: '',
                    genero_colegial: '',
                    promocion_colegial: '',
                    pasillo_colegial: '',
                    tieneCargo_colegial: false,
                    telefono_colegial: '',
                    correo_colegial: ''
                });
                setArchivoImagen(null);
                setNombreArchivoImagen('');
                actualizarLista();
            } else {
                setMensajeError(result.message);
            }
        } catch (error) {
            setMensajeError('Error al añadir colegial');
        }
    };

    const handleChangeCampo = (campo, valor) => {
        setNuevoColegial(prevColegial => ({
            ...prevColegial,
            [campo]: valor
        }));
    };

    return (
        <div className="container col-xl-6 col-lg-7 col-md-8 bdr-1 rounded p-xl-2 p-lg-1 p-md-0 position-fixed" style={{ fontWeight: '3rem', background: '#D9D9D9', top: '44%', left: '50%', transform: 'translate(-50%, -40%)', zIndex: '5' }}>
            <div className="row-fluid pb-0 d-flex">
                <div className="container col-12 pb-1 text-start" style={{ borderBottom: '2px solid white' }}>
                    <span className="customFontSizeTitulos text-black"> Añadir colegiales </span>
                    <img
                        src={cruzNegra}
                        alt="login-icon"
                        className="card-img-top"
                        style={{ position: 'absolute', height: '3rem', width: '3rem', cursor: 'pointer', top: 0, right: 0 }}
                        onClick={onClose}
                    />
                </div>
            </div>
            <div className='container-fluid pb-1 pt-1'>
                {!mostrarFormularioManual ? (
                    <>
                        <form onSubmit={(e) => { e.preventDefault(); manejarAñadirColegiales(); }}>
                            <span className='text-black customFontSizeTextoPequeño pt-2'>Puedes añadir colegiales directamente desde una excel, el
                                formato de la excel debe ser el siguiente (cada columna es un atributo): <br /><br /> foto, nombre, apellidos, estudios, habitación, género, promoción
                                pasillo, tieneCargo (0 no tiene, 1 si tiene), telefono, correo. <br /><br />  </span>
                            <span className='text-black customFontSizeTextoPequeño' style={{ fontWeight: 'bold' }}>Insertar datos (csv, xlsx o xls). </span>
                            <input
                                type="file"
                                className="form-control-file"
                                accept=".csv, .xlsx, .xls"
                                onChange={manejarArchivoDatos}
                            />
                            <button type="submit" className='text-white mt-3' style={{ backgroundColor: 'Black', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }}>
                                Añadir colegiales
                            </button>
                        </form>
                        <div className="text-center mt-4">
                            <span className='text-black customFontSizeTextoPequeño' style={{ fontWeight: 'bold' }}>Insertar un colegial manualmente: </span>
                            <button className='btn text-white ml-2' style={{ backgroundColor: 'Black', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }} onClick={() => setMostrarFormularioManual(true)}>
                                Insertar colegial
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <form onSubmit={(e) => { e.preventDefault(); manejarAñadirColegial(); }}>
                            <div className="d-flex flex-wrap">
                                {Object.keys(nuevoColegial).map((campo, index) => (
                                    <div className="form-group col-md-6" key={index} style={{textTransform: 'capitalize'}}>
                                        <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>{campo.replace('_colegial', '').replace(/_/g, ' ')}:</label>
                                        {campo === 'tieneCargo_colegial' ? (
                                            <select
                                                className="form-control"
                                                value={nuevoColegial[campo]}
                                                onChange={(e) => handleChangeCampo(campo, e.target.value)}
                                            >
                                                <option value="false">No</option>
                                                <option value="true">Sí</option>
                                            </select>
                                        ) : campo === 'genero_colegial' ? (
                                            <select
                                                className="form-control"
                                                value={nuevoColegial[campo]}
                                                onChange={(e) => handleChangeCampo(campo, e.target.value)}
                                            >
                                                <option value="">Seleccionar</option>
                                                <option value="Hombre">Hombre</option>
                                                <option value="Mujer">Mujer</option>
                                                <option value="Otro">Otro</option>
                                            </select>
                                        ) : campo === 'promocion_colegial' ? (
                                            <select
                                                className="form-control"
                                                value={nuevoColegial[campo]}
                                                onChange={(e) => handleChangeCampo(campo, e.target.value)}
                                            >
                                                <option value="">Seleccionar</option>
                                                <option value="Primera">Primera</option>
                                                <option value="Segunda">Segunda</option>
                                                <option value="Tercera">Tercera</option>
                                                <option value="Cuarta">Cuarta</option>
                                                <option value="Quinta">Quinta o más</option>
                                            </select>
                                        ) : campo === 'pasillo_colegial' ? (
                                            <select
                                                className="form-control"
                                                value={nuevoColegial[campo]}
                                                onChange={(e) => handleChangeCampo(campo, e.target.value)}
                                            >
                                                <option value="">Seleccionar</option>
                                                <option value="100s">100s</option>
                                                <option value="200s">200s</option>
                                                <option value="300s">300s</option>
                                                <option value="400s">400s</option>
                                            </select>
                                        ) : campo === 'correo_colegial' ? (
                                            <input
                                                type="email"
                                                className="form-control"
                                                value={nuevoColegial[campo]}
                                                onChange={(e) => handleChangeCampo(campo, e.target.value)}
                                            />
                                        ) : (
                                            <input
                                                type="text"
                                                className="form-control"
                                                value={nuevoColegial[campo]}
                                                onChange={(e) => handleChangeCampo(campo, e.target.value)}
                                            />
                                        )}
                                    </div>
                                ))}
                                <div className="form-group col-md-6">
                                    <label className="text-black customFontSizeTextoPequeño" style={{ fontWeight: 'bold' }}>Foto colegial:</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        accept=".jpg, .jpeg, .png"
                                        onChange={manejarArchivosImagenes}
                                    />
                                </div>
                            </div>
                            <button type="submit" className='btn text-white' style={{ backgroundColor: 'Black', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }}>
                                Añadir colegial
                            </button>
                        </form>
                        <button className='btn text-black mt-3' style={{ backgroundColor: '#D9D9D9', borderRadius: '50px', cursor: 'pointer', padding: '5px 10px' }} onClick={() => handleFormularioManual()}>
                            Volver
                        </button>
                    </>
                )}
                {mensajeError && <div className="alert alert-danger mt-3" role="alert">{mensajeError}</div>}
                {mensajeExito && <div className="alert alert-success mt-3" role="alert">{mensajeExito}</div>}
            </div>
        </div>
    );
};

export default VentanaAñadirColegiales;
